(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.RealmCommunityVideos = api;
    const start = () => api.mount(root.document, root);
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const playerOrigin = 'https://www.tiktok.com';
  const validVideoId = value => /^\d{16,20}$/.test(String(value || ''));
  function playerURL(id) {
    if (!validVideoId(id)) return null;
    const query = new URLSearchParams({ autoplay: '1', muted: '1', loop: '1', controls: '0', play_button: '0', progress_bar: '0', volume_control: '0', fullscreen_button: '0', timestamp: '0', description: '0', music_info: '0', rel: '0', closed_caption: '1' });
    return `${playerOrigin}/player/v1/${id}?${query}`;
  }
  function railState(rail) {
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const left = Math.max(0, Math.min(maximum, rail.scrollLeft));
    return { left, maximum, previous: left > 2, next: left < maximum - 2 };
  }
  function mediaVisibility(rect, rail, viewport) {
    const width = Math.min(rect.right, rail.right, viewport.width) - Math.max(rect.left, rail.left, 0);
    const height = Math.min(rect.bottom, rail.bottom, viewport.height) - Math.max(rect.top, rail.top, 0);
    return { viewport: rect.bottom > 10 && rect.top < viewport.height - 10, rail: width > 10 && height > 10,
      near: rect.bottom > -240 && rect.top < viewport.height + 240 && rect.right > rail.left - 160 && rect.left < rail.right + 160 };
  }
  function createInlinePlayer(document, window, record, elements) {
    const { frame, status, play, retry } = elements;
    let iframe = null, timer = null, generation = 0, ready = false, playing = false, failed = false, blocked = false, manual = false, destroyed = false;
    let environment = { near: false, viewport: false, rail: false, visible: true, overlay: false, paused: false, reduced: false, pageActive: true };
    const eligible = () => environment.viewport && environment.rail && environment.visible && !environment.overlay && !environment.paused && environment.pageActive && (!environment.reduced || manual);
    function clearTimer() { if (timer !== null) window.clearTimeout(timer); timer = null; }
    function message(type) { try { iframe?.contentWindow?.postMessage({ type, 'x-tiktok-player': true }, playerOrigin); } catch (_) {} }
    function render() {
      frame.dataset.playerState = failed ? 'error' : blocked ? 'blocked' : playing ? 'playing' : iframe ? ready ? 'paused' : 'loading' : 'poster';
      const playHidden = !validVideoId(record.id) || failed || !(blocked || environment.reduced && !manual);
      if (play.hidden !== playHidden) play.hidden = playHidden;
      if (retry.hidden !== !failed) retry.hidden = !failed;
      const text = failed ? status.dataset.error : blocked ? status.dataset.blocked : '';
      if (status.textContent !== text) status.textContent = text;
      status.setAttribute?.('aria-live', manual ? 'polite' : 'off');
      frame.setAttribute?.('aria-busy', String(Boolean(iframe && !ready && !failed && !blocked)));
    }
    function unload() {
      generation++; clearTimer(); message('pause'); iframe?.remove(); iframe = null; ready = false; playing = false;
      frame.replaceChildren(); delete frame.dataset.playerReady;
    }
    function fail(autoplay = false) {
      clearTimer(); message('pause'); playing = false;
      if (autoplay) blocked = true;
      else { failed = true; unload(); }
      render();
    }
    function expectPlaying() {
      if (timer !== null || !eligible() || failed || blocked) return;
      const token = generation;
      timer = window.setTimeout(() => { if (token === generation && eligible() && !playing) fail(ready && !manual); }, 12000);
    }
    function load() {
      const url = playerURL(record.id);
      if (!url || iframe || failed || blocked) return;
      iframe = document.createElement('iframe');
      iframe.title = record.creator;
      iframe.allow = 'autoplay; encrypted-media';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.tabIndex = -1;
      iframe.src = url;
      const token = generation;
      iframe.addEventListener('error', () => { if (token === generation) fail(); });
      frame.appendChild(iframe);
      const current = generation;
      timer = window.setTimeout(() => { if (current === generation && iframe && eligible() && !playing) fail(ready && !manual); }, 12000);
    }
    function synchronize() {
      if (destroyed) return;
      if (!environment.near || !eligible()) { if (iframe) unload(); render(); return; }
      if (!iframe) load();
      if (eligible() && !failed && !blocked) {
        if (ready && !playing) message('play');
        expectPlaying();
      } else { message('pause'); playing = false; clearTimer(); }
      render();
    }
    function receive(event) {
      if (!iframe || event.origin !== playerOrigin || event.source !== iframe.contentWindow || event.data?.['x-tiktok-player'] !== true) return;
      if (event.data.type === 'onPlayerReady') { ready = true; synchronize(); }
      else if (event.data.type === 'onStateChange') {
        const state = Number(event.data.value);
        if (![-1, 0, 1, 2, 3].includes(state)) return;
        if (state === 1) {
          ready = true;
          frame.dataset.playerReady = 'true';
          if (eligible() && !failed && !blocked) { playing = true; clearTimer(); }
          else { message('pause'); playing = false; }
        } else {
          playing = false;
          if (state === 0 && eligible() && !failed && !blocked) message('play');
          expectPlaying();
        }
        render();
      } else if (event.data.type === 'onPlayerError') fail(Number(event.data.value?.errorCode) === 3002);
      else if (event.data.type === 'onError') fail();
    }
    window.addEventListener('message', receive);
    render();
    return {
      setEnvironment(next) {
        if (next.reduced === true && !environment.reduced) manual = false;
        environment = { ...environment, ...next }; synchronize();
      },
      playExplicit() { if (destroyed) return; manual = true; blocked = false; failed = false; environment.paused = false; synchronize(); },
      retry() { if (destroyed) return; manual = true; blocked = false; failed = false; environment.paused = false; unload(); synchronize(); },
      unload: () => { unload(); render(); },
      destroy: () => { destroyed = true; unload(); window.removeEventListener('message', receive); },
      get manual() { return manual; },
      get playing() { return playing; },
      get hasFrame() { return Boolean(iframe); }
    };
  }
  function bind(section, document, window) {
    if (section._realmCommunityCleanup) return;
    const rail = section.querySelector('[data-community-rail]');
    const motion = section.querySelector('[data-community-motion]');
    const previous = section.querySelector('[data-community-step="-1"]'), next = section.querySelector('[data-community-step="1"]');
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const records = Array.from(section.querySelectorAll('[data-community-card]')).filter(card => validVideoId(card.dataset.communityVideoId)).map(card => ({
      card, media: card.querySelector('[data-community-media]'), nearRail: null,
      player: createInlinePlayer(document, window, { id: card.dataset.communityVideoId, creator: card.dataset.communityCreator }, {
        frame: card.querySelector('[data-community-frame]'), status: card.querySelector('[data-community-status]'), play: card.querySelector('[data-community-play]'), retry: card.querySelector('[data-community-retry]')
      })
    }));
    let paused = false, pageActive = true, sectionNear = null, frame = null, drag = null, dragged = false, opening = false, destroyed = false, openingFrame = null;
    function update() {
      if (destroyed) return;
      frame = null;
      const position = railState(rail); previous.disabled = !position.previous; next.disabled = !position.next;
      const bounds = rail.getBoundingClientRect();
      const overlay = opening || Boolean(window.RealmIntroduction?.active) || Boolean(document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])'));
      for (const record of records) {
        const geometry = mediaVisibility(record.media.getBoundingClientRect(), bounds, { width: window.innerWidth, height: window.innerHeight });
        record.player.setEnvironment({ near: geometry.near && sectionNear !== false && record.nearRail !== false, viewport: geometry.viewport, rail: geometry.rail, visible: document.visibilityState !== 'hidden', overlay, paused, reduced: Boolean(reduce?.matches), pageActive });
      }
      const motionHidden = records.length === 0 || Boolean(reduce?.matches && !records.some(record => record.player.manual));
      if (motion.hidden !== motionHidden) motion.hidden = motionHidden;
      const label = paused ? motion.dataset.resume : motion.dataset.pause;
      if (motion.textContent !== label) motion.textContent = label;
      motion.setAttribute('aria-pressed', String(paused));
    }
    function schedule() { if (!destroyed && frame === null) frame = window.requestAnimationFrame(update); }
    function step(direction) {
      const width = rail.querySelector('[data-community-card]')?.getBoundingClientRect().width || rail.clientWidth;
      const state = railState(rail);
      rail.scrollTo({ left: Math.max(0, Math.min(state.maximum, state.left + (width + 4) * direction)), behavior: reduce?.matches ? 'auto' : 'smooth' });
    }
    function onClick(event) {
      if (dragged && rail.contains(event.target)) { dragged = false; event.preventDefault(); event.stopImmediatePropagation(); return; }
      const control = event.target.closest('[data-community-step], [data-community-motion], [data-community-play], [data-community-retry]');
      if (!control) return;
      event.preventDefault();
      if (control.matches('[data-community-step]')) step(Number(control.dataset.communityStep));
      else if (control === motion) { paused = !paused; update(); }
      else {
        const record = records.find(item => item.card === control.closest('[data-community-card]'));
        if (!record) return;
        paused = false; update();
        if (control.matches('[data-community-retry]')) record.player.retry();
        else record.player.playExplicit();
        update();
      }
    }
    function onKey(event) {
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : railState(rail).maximum, behavior: reduce?.matches ? 'auto' : 'smooth' });
      else step(event.key === 'ArrowLeft' ? -1 : 1);
    }
    function onDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,a')) return;
      dragged = false; drag = { id: event.pointerId, x: event.clientX, left: rail.scrollLeft, moved: false };
    }
    function onMove(event) {
      if (!drag || event.pointerId !== drag.id) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && Math.abs(delta) < 8) return;
      if (!drag.moved) { drag.moved = true; rail.setPointerCapture?.(event.pointerId); rail.dataset.dragging = 'true'; }
      event.preventDefault(); rail.scrollLeft = drag.left - delta;
    }
    function onUp(event) {
      if (!drag || event.pointerId !== drag.id) return;
      dragged = drag.moved;
      if (rail.hasPointerCapture?.(event.pointerId)) rail.releasePointerCapture?.(event.pointerId);
      delete rail.dataset.dragging; drag = null;
      window.setTimeout(() => { dragged = false; }, 0); schedule();
    }
    const viewportObserver = typeof window.IntersectionObserver === 'function' ? new window.IntersectionObserver(entries => { sectionNear = entries.at(-1)?.isIntersecting ?? false; schedule(); }, { rootMargin: '240px 0px', threshold: 0 }) : null;
    viewportObserver?.observe(section);
    const railObserver = typeof window.IntersectionObserver === 'function' ? new window.IntersectionObserver(entries => { for (const entry of entries) { const record = records.find(item => item.media === entry.target); if (record) record.nearRail = entry.isIntersecting; } schedule(); }, { root: rail, rootMargin: '0px 160px', threshold: 0 }) : null;
    for (const record of records) railObserver?.observe(record.media);
    const onOverlay = () => { opening = true; update(); if (openingFrame !== null) window.cancelAnimationFrame(openingFrame); openingFrame = window.requestAnimationFrame(() => { openingFrame = null; if (destroyed) return; opening = false; schedule(); }); };
    const onPageHide = () => { pageActive = false; update(); };
    const onPageShow = () => { pageActive = true; sectionNear = null; for (const record of records) record.nearRail = null; update(); };
    const onPreference = () => update();
    const observer = typeof window.MutationObserver === 'function' ? new window.MutationObserver(changes => {
      if (changes.some(change => change.target?.matches?.('dialog, [role="dialog"]'))) schedule();
    }) : null;
    observer?.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'hidden', 'aria-modal'] });
    section.addEventListener('click', onClick, true);
    rail.addEventListener('scroll', schedule, { passive: true }); rail.addEventListener('keydown', onKey);
    rail.addEventListener('pointerdown', onDown); rail.addEventListener('pointermove', onMove); rail.addEventListener('pointerup', onUp); rail.addEventListener('pointercancel', onUp);
    document.addEventListener('realm:overlay-opening', onOverlay); document.addEventListener('visibilitychange', onPreference); document.addEventListener('close', schedule, true); document.addEventListener('realm:intro-released', schedule);
    window.addEventListener('scroll', schedule, { passive: true }); window.addEventListener('resize', schedule); window.addEventListener('pagehide', onPageHide); window.addEventListener('pageshow', onPageShow); reduce?.addEventListener?.('change', onPreference);
    update();
    section._realmCommunityCleanup = () => {
      destroyed = true;
      viewportObserver?.disconnect(); railObserver?.disconnect(); observer?.disconnect();
      for (const record of records) record.player.destroy();
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (openingFrame !== null) window.cancelAnimationFrame(openingFrame);
      section.removeEventListener('click', onClick, true);
      rail.removeEventListener('scroll', schedule); rail.removeEventListener('keydown', onKey); rail.removeEventListener('pointerdown', onDown); rail.removeEventListener('pointermove', onMove); rail.removeEventListener('pointerup', onUp); rail.removeEventListener('pointercancel', onUp);
      document.removeEventListener('realm:overlay-opening', onOverlay); document.removeEventListener('visibilitychange', onPreference); document.removeEventListener('close', schedule, true); document.removeEventListener('realm:intro-released', schedule);
      window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); window.removeEventListener('pagehide', onPageHide); window.removeEventListener('pageshow', onPageShow); reduce?.removeEventListener?.('change', onPreference);
      delete section._realmCommunityCleanup;
    };
  }
  function mount(document, window) {
    if (!document || document.documentElement.dataset.realmCommunityMounted) return;
    document.documentElement.dataset.realmCommunityMounted = 'true';
    const initialize = scope => { for (const section of scope.querySelectorAll('[data-realm-community-videos]')) bind(section, document, window); };
    initialize(document);
    document.addEventListener('shopify:section:load', event => initialize(event.target));
    document.addEventListener('shopify:section:unload', event => { for (const section of event.target.querySelectorAll('[data-realm-community-videos]')) section._realmCommunityCleanup?.(); });
  }
  return { validVideoId, playerURL, railState, mediaVisibility, createInlinePlayer, bind, mount };
});
